import { Flex, Grid } from '@/components/atoms/Grid'
import Link from '@/components/atoms/link'
import ResponsiveImage from '@/components/atoms/responsiveImage'
import { InformationH2 } from '@/components/atoms/typography'
import CampaignFilter, {
  DesignationLabelMap,
  TypeLabelMap
} from '@/components/molecules/CampaignFilter'
import CategoryPathHeader from '@/components/molecules/categoryPathHeader'
import PageContentWrapper, {
  PageContentWrapperBackgroundStyle
} from '@/components/organisms/pageContentWrapper'
import { Routes } from '@/core/routes'
import {
  Campaign as CampaignModel,
  CampaignDesignation,
  CampaignType,
  Page
} from '@/network/graphql.g'
import useTranslation from 'next-translate/useTranslation'
import { FC, useEffect } from 'react'
import { useState } from 'react'
import styled from 'styled-components'
import { useRouter } from 'next/router'
import { convertQueryParamsAndRemoveEmptyGetParameters } from '@/core/toStringHelper'
import { parseFilterQueryIdsFromQuery } from '@/providers/filterProvider/parsingFilters'
import { FilterQueryItem } from 'types/filterQueryItem'
import { requestThrow401 } from '@/utils/graphQl'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { useMhub } from '@/providers/mhubProvider'
import { getMainData } from '@/utils/mainDataHelper'
import { CacheType } from '@/utils/cache/pageDataCache'

type CampaignProps = {
  campaigns: CampaignModel[]
  campaignTypes: Record<CampaignType, number>
  campaignDesignations: Record<CampaignDesignation, number>
}

const toRecord = (values: string[]) =>
  values.reduce(
    (acc, value) => ((acc[value] = true), acc),
    {} as Record<string, boolean>
  )

const Campaign: FC<CampaignProps> = ({
  campaigns,
  campaignTypes,
  campaignDesignations
}) => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const query = parseFilterQueryIdsFromQuery(router.query)
  const [typeFilter, setTypeFilter] = useState<Record<CampaignType, boolean>>(
    query.campaignType ? toRecord(Object.values(query.campaignType)) : {}
  )
  const [designationFilter, setDesignationFilter] = useState<
    Record<CampaignDesignation, boolean>
  >(
    query.campaignDesignation
      ? toRecord(Object.values(query.campaignDesignation))
      : {}
  )
  const [filteredCampaigns, setFilteredCampaigns] =
    useState<CampaignModel[]>(campaigns)

  const getActiveTypes = (typeFilter: Record<CampaignType, boolean>) =>
    Object.keys(typeFilter).filter((value) => typeFilter[value])

  const getActiveDesignations = (
    designationFilter: Record<CampaignDesignation, boolean>
  ) =>
    Object.keys(designationFilter).filter((value) => designationFilter[value])

  useEffect(() => {
    const types = getActiveTypes(typeFilter)
    const designations = getActiveDesignations(designationFilter)

    let list = [...campaigns]
    if (types.length > 0) {
      list = list.filter((item) => types.includes(item.type))
    }
    if (designations.length > 0) {
      list = list.filter((item) => designations.includes(item.designation))
    }

    setFilteredCampaigns(list)
  }, [typeFilter, designationFilter, campaigns])

  const onTypeCheckboxClick = (type: CampaignType) => {
    setTypeFilter((oldTypeFilter) => {
      return { ...oldTypeFilter, [type]: !oldTypeFilter[type] }
    })
  }

  const onDesignationCheckboxClick = (designation: CampaignDesignation) => {
    setDesignationFilter((oldDesignationFilter) => {
      return {
        ...oldDesignationFilter,
        [designation]: !oldDesignationFilter[designation]
      }
    })
  }

  useEffect(() => {
    pushFilter(typeFilter, designationFilter)
  }, [typeFilter, designationFilter])

  const pushFilter = (
    typeFilter: Record<CampaignType, boolean>,
    designationFilter: Record<CampaignDesignation, boolean>
  ) => {
    const types = getActiveTypes(typeFilter)
    const designations = getActiveDesignations(designationFilter)
    const query = {
      [FilterQueryItem.CAMPAIGN_TYPE]: types,
      [FilterQueryItem.CAMPAIGN_DESIGNATION]: designations
    }
    const stringQuery = convertQueryParamsAndRemoveEmptyGetParameters({
      query,
      allowOnly: Object.values(FilterQueryItem)
    })
    const basePath = router.asPath.split('?')[0]
    const path = `${basePath}${stringQuery ? `?${stringQuery}` : ``}`
    router.push(path, path, {
      shallow: true
    })
  }

  const { pushCampaignPage } = useMhub()
  useEffect(() => {
    pushCampaignPage()
  }, [])

  return (
    <PageContentWrapper
      mb={80}
      backgroundStyle={PageContentWrapperBackgroundStyle.normal}
    >
      <CategoryPathHeader
        paths={[{ url: Routes.campaign, title: t('Campaign.title') }]}
      />
      <InformationH2>{t('Campaign.title')}</InformationH2>
      <StyledFlex
        flexDirection={{ desktop: 'row', tablet: 'column', mobile: 'column' }}
      >
        <CampaignFilter
          title={t('Campaign.type')}
          items={campaignTypes}
          data={typeFilter}
          labelMap={TypeLabelMap}
          onCheckboxClick={onTypeCheckboxClick}
        />
        <CampaignFilter
          title={t('Campaign.designation')}
          items={campaignDesignations}
          data={designationFilter}
          labelMap={DesignationLabelMap}
          onCheckboxClick={onDesignationCheckboxClick}
        />
      </StyledFlex>
      <Grid
        mt={16}
        gridTemplateColumns={{
          desktop: '1fr 1fr 1fr',
          tablet: '1fr 1fr',
          mobile: '1fr'
        }}
        gridRowGap={20}
        gridColumnGap={18}
      >
        {filteredCampaigns.map((item) => (
          <Link key={item.id} href={item.url}>
            <StyledResponsiveImage
              width={600}
              height={280}
              imageUrl={item.desktopImage}
            />
          </Link>
        ))}
      </Grid>
    </PageContentWrapper>
  )
}

export default Campaign

export const getServerSideProps = withBaseServerSideProps(
  async ({
    sdk,
    withMainNavigation,
    withStore
  }): Promise<GetServerSideProps<CampaignProps>> => {
    const { success, response } = await requestThrow401({
      method: sdk.getCampaigns,
      variables: {
        page: Page.Campaigns,
        withMainNavigation,
        withStore
      },
      mainResponse: (response) => response?.campaigns
    })
    if (!success) {
      return {
        notFound: true
      }
    }

    return {
      props: {
        mainData: getMainData(response),
        seoTags: response?.seoTags,
        campaigns: response?.campaigns,
        campaignTypes: getCampaignTypes(response?.campaigns),
        campaignDesignations: getCampaignDesignations(response?.campaigns),
        disableDaktela: true
      }
    }
  },
  { cacheType: CacheType.Campaign }
)

const getCampaignTypes = (
  campaigns: CampaignModel[]
): Record<CampaignType, number> => {
  const types = campaigns
    .map((item) => item.type)
    .filter((item, index, array) => array.indexOf(item) === index)
    .sort()

  return types.reduce(
    (acc, type) => (
      (acc[type] = campaigns.filter(
        (campaign) => campaign.type === type
      ).length),
      acc
    ),
    {} as Record<CampaignType, number>
  )
}

const getCampaignDesignations = (
  campaigns: CampaignModel[]
): Record<CampaignDesignation, number> => {
  const designations = campaigns
    .map((item) => item.designation)
    .filter((item, index, array) => array.indexOf(item) === index)
    .sort()

  return designations.reduce(
    (acc, designation) => (
      (acc[designation] = campaigns.filter(
        (campaign) => campaign.designation === designation
      ).length),
      acc
    ),
    {} as Record<CampaignDesignation, number>
  )
}

const StyledFlex = styled(Flex)`
  border: 1px solid ${({ theme }) => theme.colors.sectionSeparator};
  border-width: 1px 0;
  column-gap: 32px;
`

const StyledResponsiveImage = styled(ResponsiveImage)`
  border-radius: ${(props) => props.theme.borderRadius}px;
`
